import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { clientQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";
import { useAuth } from "@/lib/hooks/useAuth";
import { ClientFilter } from "@/types/response/ClientType";
import { GetClientsResponse } from "@/types/response/GetClientsResponse";

type UseClientsParams = {
  clientType?: string[] | null;
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  paymentType?: string[] | null;
  search?: string;
  tenantId?: string | null;
} & ClientFilter;

/**
 * Hook to fetch a paginated list of clients with filtering, sorting, and search capabilities.
 *
 * @param params - Query parameters for filtering and pagination
 * @param params.clientType - Array of client type IDs to filter by
 * @param params.descending - Whether to sort in descending order
 * @param params.orderBy - Column name to sort by
 * @param params.page - Page number for pagination
 * @param params.pageSize - Number of items per page
 * @param params.paymentType - Array of payment type IDs to filter by
 * @param params.search - Search query string
 * @param params.tenantId - Tenant ID to filter clients by
 *
 * @returns React Query result containing clients data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data: clients, isLoading } = useClients({
 *   page: 1,
 *   pageSize: 10,
 *   search: "Acme",
 *   tenantId: "123"
 * });
 * ```
 */
export function useClients(params: UseClientsParams) {
  const { context } = useAuth();

  return useQuery<GetClientsResponse, AxiosError>({
    queryKey: clientQueryKeys.list({
      clientType: params.clientType,
      descending: params.descending,
      orderBy: params.orderBy,
      page: params.page,
      pageSize: params.pageSize,
      paymentType: params.paymentType,
      search: params.search,
      tenantId: params.tenantId,
    }),
    queryFn: () =>
      instance
        .get(`/api/v2/Clients`, {
          params: {
            clientType: params.clientType,
            descending: params.descending,
            orderBy: params.orderBy,
            page: params.page,
            pageSize: params.pageSize,
            paymentType: params.paymentType,
            search: params.search,
            tenantId: params.tenantId || undefined,
          },
        })
        .then((res) => res.data),
    placeholderData: keepPreviousData,
    refetchOnReconnect: false,
    enabled: context === "admin",
    staleTime: 20 * 60 * 1000,
  });
}
